import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useProperty,
  usePropertyField,
  usePropertyAddressUrl,
  useCollectionBinding,
  usePrimaryColor,
  usePage,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import pagesData from '../pages.json';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  MultihubLogoMonoWhite,
  MultihubLogoMonoBlack,
  repliWhiteLogoUrl,
} from '@vibesites/assets';
import repliBlackLogoUrl from '../assets/repli_black.svg';
import { getContrastTextColor, TEXT_ON_PRIMARY_LIGHT } from '../utils/luminance';

// ============================================================================
// Dynamic Navigation from pages.json
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (indented row inside the slide-in drawer). */
  parent?: string;
}

/** Page aliases that should never appear in the main navigation */
const NAV_EXCLUDED_ALIASES = new Set([
  'home',
  'privacy-policy',
  'sitemap',
  'accessibility',
  'apply-now',
  'thank-you',
  'resident-access',
  'schedule-a-tour',
  'hyperlocal',
  'reviews',
]);

interface NavLink {
  label: string;
  href: string;
  /** Sub-nav items rendered as indented rows inside the drawer. Populated
      when other pages declare `parent: <this alias>`. */
  children?: NavLink[];
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

/**
 * Build navigation links from pages.json.
 * Only includes pages that are visible and not in the exclusion list.
 * Sorted by position. Pages declaring `parent: <alias>` are nested as
 * `children` of their parent entry. Orphaned children (parent missing or
 * hidden) fall back to the top level so a page is never silently dropped.
 *
 * `hasInteractiveLeasing` controls the dynamic visibility of the Interactive
 * Map page (ApartmentSync / Repli360 widget keyed by two per-property custom
 * fields). See the SSG-side hide + noindex logic in ssg-entry.ts which this
 * mirrors so navigation, sitemap, and crawler hints stay consistent.
 */
function buildNavLinks(hasVirtualTours: boolean, hasInteractiveLeasing: boolean): NavLink[] {
  const eligible = Object.entries(pagesData as Record<string, PageEntry>)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position);

  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();
  const topLevel: NavLink[] = [];

  for (const [alias, page] of eligible) {
    linksByAlias.set(alias, { label: page.name, href: page.path });
  }

  for (const [alias, page] of eligible) {
    const link = linksByAlias.get(alias)!;
    if (page.parent && eligibleAliases.has(page.parent) && page.parent !== alias) {
      const parentLink = linksByAlias.get(page.parent)!;
      (parentLink.children ??= []).push(link);
    } else {
      topLevel.push(link);
    }
  }

  return topLevel;
}

/** Add a leading "/" + normalize a trailing slash so two paths from different
    sources (pages.json `path`, runtime `page.path`, window.location.pathname)
    compare equal regardless of trailing-slash convention. */
function normalizePath(p: string | undefined | null): string {
  if (!p) return '';
  let s = p.trim();
  if (!s.startsWith('/')) s = `/${s}`;
  if (s.length > 1 && s.endsWith('/')) s = s.slice(0, -1);
  return s;
}

// ============================================================================
// Office Hours formatting (mirrors Footer.tsx)
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

interface GroupedHours {
  label: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  // Index the provided entries by their canonical day key so we can look each
  // day up when walking the full week below.
  const byDay = new Map<string, OfficeHour>();
  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (DAY_ORDER.includes(key) && !byDay.has(key)) byDay.set(key, entry);
  }

  const groups: GroupedHours[] = [];

  // Walk every day of the week. Days without provided hours (e.g. Sunday)
  // render as "Closed" rather than being omitted.
  for (const key of DAY_ORDER) {
    const entry = byDay.get(key);
    const dayShortLabel = DAY_SHORT_LABELS[key];
    const isClosed =
      !entry || (!hasValue(entry.open_time) && !hasValue(entry.close_time));
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const dash = prev.label.indexOf(' - ');
      const start = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${start} - ${dayShortLabel}`;
    } else {
      groups.push({ label: dayShortLabel, hours: hoursStr });
    }
  }

  return groups;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Address row inside the slide-in drawer ("Visit Us"). The URL has no
// defaultField — it defaults to usePropertyAddressUrl() at render time
// (mobile-safe Google Maps URL built from place / business / full address /
// lat-lon fallbacks). An explicit editor override wins.
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    address: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// SVG Icons
// ============================================================================

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// ============================================================================
// Main Header Component
// ----------------------------------------------------------------------------
// STRUCTURE: A simple white, pinned (fixed) header bar:
//   [hamburger]  [logo] ........... [Resident Access] [Apply Now]
// All page navigation lives inside an off-canvas drawer that slides in from
// the LEFT when the hamburger is toggled. The drawer is painted in the
// property's primary color and hosts the nav links, office hours, the two
// CTAs, the address ("Visit Us"), the phone ("Let's Talk"), and the
// Multihub / Repli credits.
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  // Subtle shadow once the page scrolls a little past the top.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Current page path — drives the active-link underline in the drawer.
  const pageCtx = usePage();
  const currentPath = useMemo(
    () =>
      normalizePath(
        typeof window !== 'undefined' ? window.location.pathname : pageCtx?.path,
      ),
    [pageCtx?.path],
  );

  // Credit-logo variant (white vs black) follows the same contrast logic the
  // Footer uses, so the Multihub / Repli marks stay legible on the drawer's
  // primary-color background regardless of how light/dark the brand color is.
  const primaryColor = usePrimaryColor();
  const useWhiteOnPrimary = getContrastTextColor(primaryColor) === TEXT_ON_PRIMARY_LIGHT;

  // Virtual tours — controls nav-link visibility
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Interactive Map — drops out of the nav when the property has no
  // ApartmentSync (Repli360 / RRAC) IDs to drive the "interactive leasing
  // view" widget. We require BOTH the JavaScript ID and the Config ID — a
  // property with only one populated is a half-configured state. Mirrors the
  // SSG-side hide + noindex logic in ssg-entry.ts.
  const propertyForLeasing = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = propertyForLeasing?.custom_apartmentsync_javascript_id as string | null | undefined;
  const apartmentSyncConfigId = propertyForLeasing?.custom_apartmentsync_config_id as string | null | undefined;

  const interactiveMapShowIfExists = useContentToken('header.interactive_map_show_if_exists');
  const hasInteractiveMapOverride = !!(interactiveMapShowIfExists && String(interactiveMapShowIfExists).trim());

  const hasInteractiveLeasing =
    (!!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
      !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim())) ||
    hasInteractiveMapOverride;

  const navLinks = useMemo(
    () => buildNavLinks(hasVirtualTours, hasInteractiveLeasing),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  // Content tokens
  const phone = useContentToken('header.phone');

  // Slot groups
  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const monoWhiteLogo = usePropertyField('property_mono_white_logo');
  const monoBlackLogo = usePropertyField('property_mono_black_logo');
  const secondaryLogo = usePropertyField('property_secondary_logo');

  // Bar sits on white → prefer the dark/mono-black logo. Drawer sits on the
  // primary color → prefer the contrasting mono logo (white on dark primary,
  // black on light primary). Both fall back to whatever the editor picked.
  const barLogoImage = monoBlackLogo || logoRaw.image;
  const drawerLogoImage = (useWhiteOnPrimary ? monoWhiteLogo : monoBlackLogo) || logoRaw.image;

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  const propertyAddressUrl = usePropertyAddressUrl();

  const officeHours = useCollectionBinding<OfficeHour>('header.office_hours', { collection: 'office_hours' });
  const groupedHours = officeHours && officeHours.length > 0 ? groupOfficeHours(officeHours) : [];

  // Google Fonts — heading + body (Josefin Sans), accent (Dancing Script), and
  // Josefin Slab (the slab/western display face used by content section titles
  // like PropertyOverview). Loaded globally from the Header so every page
  // renders the theme's typography. (The hero titles stay on Josefin Sans.)
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="homestead-fonts"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Josefin+Sans:wght@300;400;500;600;700&family=Josefin+Slab:wght@300;400;500;600;700&family=Dancing+Script:wght@500;600;700&display=swap';
    link.setAttribute('data-vibe-font', 'homestead-fonts');
    document.head.appendChild(link);
  }, []);

  // Measure header height for the layout spacer (the header is fixed, so the
  // page needs a placeholder of equal height to sit beneath it).
  const updateHeaderHeight = useCallback(() => {
    if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
  }, []);

  useEffect(() => {
    updateHeaderHeight();
    const observer = new ResizeObserver(updateHeaderHeight);
    if (headerRef.current) observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, [updateHeaderHeight]);

  // Lock body scroll while the drawer is open + close on Escape.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      {/* ============================================================== */}
      {/* Pinned white header bar                                        */}
      {/* ============================================================== */}
      <header
        ref={headerRef}
        className={`fixed left-0 right-0 top-0 z-50 w-full bg-[var(--vibe-color-background)] transition-shadow duration-300${scrolled ? ' header-scrolled' : ''}`}
        style={{ paddingTop: 'var(--offer-banner-height, 0px)' }}
      >
        {/* Editor-only control. Holds the name of the property field whose
            presence exposes the Interactive Map page in the nav. Rendered
            visually hidden because it's configuration, not display copy. */}
        <EditableToken
          path="header.interactive_map_show_if_exists"
          type="text"
          label="Interactive Map: Show If Exists"
          description="Expose the Interactive Map page in the navigation if this token or field contains a value (e.g. custom_engrain_sightmap_url). Leave blank to hide."
          as="span"
          className="sr-only"
        >
          {interactiveMapShowIfExists || ''}
        </EditableToken>

        <div className="header-bar px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-20 lg:h-24 gap-4">
            {/* Hamburger — LEFT */}
            <button
              onClick={() => setMenuOpen(true)}
              className="relative w-10 h-10 flex flex-col items-center justify-center gap-[5px] flex-shrink-0 -ml-2"
              aria-label="Open menu"
              aria-expanded={menuOpen}
              aria-haspopup="true"
            >
              <span className="block w-6 h-0.5 bg-[var(--vibe-color-text)]" />
              <span className="block w-6 h-0.5 bg-[var(--vibe-color-text)]" />
              <span className="block w-6 h-0.5 bg-[var(--vibe-color-text)]" />
            </button>

            {/* Logo — centered on mobile (CTAs hidden there), left-aligned from sm up */}
            <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef} className="mx-auto sm:mx-0 flex-shrink-0">
              <a href="/" className="block">
                {barLogoImage ? (
                  <VibesitesImage
                    url={barLogoImage}
                    alt={logoRaw.alt || 'Community Logo'}
                    className="h-12 lg:h-14 w-auto object-contain"
                  />
                ) : (
                  <div className="font-heading text-2xl lg:text-3xl font-bold tracking-wide text-[var(--vibe-color-text)]">
                    The Rise
                  </div>
                )}
              </a>
            </EditableSlotGroup>

            {/* Right group — CTAs (hidden on mobile; they live in the drawer menu) */}
            <div className="hidden sm:flex items-center gap-2 sm:gap-3 flex-shrink-0 ml-auto">
              <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                <a
                  href={residentAccessLink.url || '/resident-access'}
                  target={residentAccessLink._target || '_self'}
                  className="header-cta header-cta--solid"
                >
                  {residentAccessLink.label || 'Resident Access'}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                <a
                  href={applyNowLink.url || '/apply-now'}
                  target={applyNowLink._target || '_self'}
                  className="header-cta header-cta--solid"
                >
                  {applyNowLink.label || 'Apply Now'}
                </a>
              </EditableSlotGroup>
            </div>

            {/* Mobile spacer mirroring the hamburger so the centered logo sits in the true center of the bar */}
            <div aria-hidden="true" className="w-10 -mr-2 flex-shrink-0 sm:hidden" />
          </div>
        </div>
      </header>

      {/* Layout spacer beneath the fixed bar. Falls back to the bar's fixed
          height classes before the measured height is available (SSG/first
          paint) so there's no content jump on hydration. */}
      <div
        aria-hidden="true"
        className={headerHeight ? '' : 'h-20 lg:h-24'}
        style={headerHeight ? { height: `${headerHeight}px` } : undefined}
      />

      {/* ============================================================== */}
      {/* Off-canvas drawer — slides in from the LEFT                    */}
      {/* ============================================================== */}
      <div
        className={`fixed inset-0 z-[60] transition-[visibility] duration-300 ${
          menuOpen ? 'visible' : 'invisible pointer-events-none'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
      >
        {/* Backdrop */}
        <div
          onClick={closeMenu}
          className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${
            menuOpen ? 'opacity-100' : 'opacity-0'
          }`}
        />

        {/* Panel — offset below the SpecialOfferBanner (which is fixed at
            top:0 with the same z-index) so its content, and the close button
            in particular, never tuck underneath the banner. Tracks the live
            `--offer-banner-height` the banner publishes, so it snaps back to
            the top when the banner is dismissed / absent. */}
        <aside
          className={`drawer-panel absolute left-0 w-full max-w-[1140px] overflow-y-auto shadow-2xl transition-transform duration-300 ease-out ${
            menuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          style={{
            top: 'var(--offer-banner-height, 0px)',
            height: 'calc(100% - var(--offer-banner-height, 0px))',
            backgroundColor: 'var(--vibe-color-primary)',
            color: 'var(--vibe-color-text-on-primary)',
          }}
        >
          <div className="relative flex min-h-full flex-col px-7 py-7 sm:px-10 sm:py-9 lg:px-14 lg:py-12">
            {/* Close */}
            <button
              onClick={closeMenu}
              className="drawer-close absolute top-6 left-6 sm:top-8 sm:left-9 lg:top-10 lg:left-12 w-10 h-10 flex items-center justify-center"
              aria-label="Close menu"
            >
              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>

            {/* Main two-zone content */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-10 pt-16 lg:pt-12">
              {/* ---- Left zone: logo, nav links, office hours ---- */}
              <div className="lg:pr-8">
                <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
                  <a href="/" onClick={closeMenu} className="inline-block">
                    {drawerLogoImage ? (
                      <VibesitesImage
                        url={drawerLogoImage}
                        alt={logoRaw.alt || 'Community Logo'}
                        className="h-12 lg:h-14 w-auto object-contain"
                      />
                    ) : (
                      <div className="font-heading text-2xl lg:text-3xl font-bold tracking-wide">The Rise</div>
                    )}
                  </a>
                </EditableSlotGroup>

                <nav className="mt-8 lg:mt-10 flex flex-col" role="navigation" aria-label="Main navigation">
                  {navLinks.map((link) => {
                    const active = normalizePath(link.href) === currentPath;
                    return (
                      <React.Fragment key={link.href}>
                        <a
                          href={link.href}
                          onClick={closeMenu}
                          aria-current={active ? 'page' : undefined}
                          className={`drawer-nav-link font-heading text-lg lg:text-xl py-2.5 ${active ? 'is-active' : ''}`}
                        >
                          {link.label}
                        </a>
                        {link.children?.map((child) => {
                          const childActive = normalizePath(child.href) === currentPath;
                          return (
                            <a
                              key={child.href}
                              href={child.href}
                              onClick={closeMenu}
                              aria-current={childActive ? 'page' : undefined}
                              className={`drawer-nav-link font-heading text-base lg:text-lg py-2 pl-5 opacity-90 ${childActive ? 'is-active' : ''}`}
                            >
                              {child.label}
                            </a>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </nav>

                {groupedHours.length > 0 && (
                  <div className="mt-10 lg:mt-12">
                    <h3 className="font-heading text-base lg:text-lg font-semibold mb-3">Office Hours</h3>
                    <dl className="space-y-1.5 max-w-[300px]">
                      {groupedHours.map((group, index) => (
                        <div key={index} className="flex justify-between gap-6 font-body text-sm opacity-85">
                          <dt>{group.label}</dt>
                          <dd className="text-right">{group.hours}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}
              </div>

              {/* ---- Right zone: CTAs, address, phone, secondary logo ---- */}
              <div className="flex flex-col gap-8">
                {/* CTAs — always side-by-side, equal width */}
                <div className="flex flex-row gap-3">
                  <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef} className="flex-1 min-w-0">
                    <a
                      href={residentAccessLink.url || '/resident-access'}
                      target={residentAccessLink._target || '_self'}
                      onClick={closeMenu}
                      className="drawer-cta"
                    >
                      {residentAccessLink.label || 'Resident Access'}
                    </a>
                  </EditableSlotGroup>
                  <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef} className="flex-1 min-w-0">
                    <a
                      href={applyNowLink.url || '/apply-now'}
                      target={applyNowLink._target || '_self'}
                      onClick={closeMenu}
                      className="drawer-cta"
                    >
                      {applyNowLink.label || 'Apply Now'}
                    </a>
                  </EditableSlotGroup>
                </div>

                {/* Visit Us */}
                <div>
                  <h3 className="font-heading text-lg lg:text-xl font-semibold mb-2">Visit Us</h3>
                  <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                    <a
                      href={addressLink.url || propertyAddressUrl || '#'}
                      target={addressLink._target || '_blank'}
                      rel="noopener noreferrer"
                      onClick={closeMenu}
                      className="drawer-text-link inline-flex items-start gap-2 font-body text-sm lg:text-base opacity-90"
                    >
                      <MapPinIcon className="w-4 h-4 mt-1 flex-shrink-0" />
                      <span className="whitespace-pre-line">{addressLink.address}</span>
                    </a>
                  </EditableSlotGroup>
                </div>

                {/* Let's Talk */}
                <div>
                  <h3 className="font-heading text-lg lg:text-xl font-semibold mb-2">Let&apos;s Talk</h3>
                  <a
                    href={`tel:${(phone || '').replace(/[^+\d]/g, '')}`}
                    className="dniphone dniphonehref drawer-text-link inline-flex items-center gap-2 font-body text-sm lg:text-base opacity-90"
                  >
                    <PhoneIcon className="w-4 h-4 flex-shrink-0" />
                    <EditableToken
                      path="header.phone"
                      defaultField="property_call_tracking_phone"
                      defaultValue="(555) 555-5555"
                      as="span"
                    >
                      {formatPhone(phone)}
                    </EditableToken>
                  </a>
                </div>

                {/* Secondary / company logo */}
                {secondaryLogo ? (
                  <div className="pt-1">
                    <VibesitesImage
                      url={secondaryLogo}
                      alt="Community Logo"
                      className="h-10 lg:h-12 w-auto object-contain"
                    />
                  </div>
                ) : null}
              </div>
            </div>

            {/* ---- Credits ---- */}
            <div className="mt-auto pt-10 lg:pt-14">
              <div className="flex flex-wrap items-end gap-x-16 gap-y-6">
                <a
                  href="https://www.multihub.io/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block opacity-90 hover:opacity-100 transition-opacity"
                >
                  <span className="block font-body text-[10px] uppercase tracking-[0.15em] opacity-70 mb-2">Website Powered By</span>
                  {useWhiteOnPrimary ? (
                    <MultihubLogoMonoWhite height={26} alt="Multihub" />
                  ) : (
                    <MultihubLogoMonoBlack height={26} alt="Multihub" />
                  )}
                </a>
                <a
                  href="https://www.repli360.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block opacity-90 hover:opacity-100 transition-opacity"
                >
                  <span className="block font-body text-[10px] uppercase tracking-[0.15em] opacity-70 mb-2">Website Designed By</span>
                  <VibesitesImage
                    url={useWhiteOnPrimary ? repliWhiteLogoUrl : repliBlackLogoUrl}
                    alt="Repli Logo"
                    height={26}
                    className="h-[26px] w-auto"
                  />
                </a>
              </div>
            </div>
          </div>
        </aside>
      </div>

      <style>{`
        /* Pinned white header bar */
        .header-bar {
          border-bottom: 1px solid var(--vibe-color-border);
        }
        .header-scrolled {
          box-shadow:
            0 1px 3px rgba(0,0,0,0.05),
            0 4px 14px rgba(0,0,0,0.04);
        }

        /* Header CTA buttons */
        .header-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.5rem 0.7rem;
          font-family: var(--vibe-font-body);
          font-size: 0.6875rem;
          font-weight: 600;
          letter-spacing: 0.03em;
          text-transform: none;
          border-radius: 2px;
          white-space: nowrap;
          transition: background-color 0.25s ease, color 0.25s ease, opacity 0.25s ease;
        }
        @media (min-width: 640px) {
          .header-cta { padding: 0.95rem 1.75rem; font-size: 0.875rem; }
        }
        .header-cta--solid {
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary);
        }
        .header-cta--solid:hover {
          background: color-mix(in srgb, var(--vibe-color-secondary), black 12%);
        }

        /* ---- Drawer ---- */
        .drawer-close {
          color: var(--vibe-color-text-on-primary);
          opacity: 0.85;
          transition: opacity 0.2s ease, transform 0.2s ease;
        }
        .drawer-close:hover { opacity: 1; transform: rotate(90deg); }

        /* Drawer nav links: animated underline on hover, persistent underline
           when active (current page). */
        .drawer-nav-link {
          color: var(--vibe-color-text-on-primary);
          width: fit-content;
          text-decoration-line: underline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 6px;
          transition: text-decoration-color 0.25s ease, text-underline-offset 0.25s ease, opacity 0.2s ease;
        }
        .drawer-nav-link:hover,
        .drawer-nav-link.is-active {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
          opacity: 1;
        }

        /* Drawer address / phone links */
        .drawer-text-link {
          color: var(--vibe-color-text-on-primary);
          transition: opacity 0.2s ease;
        }
        .drawer-text-link:hover { opacity: 1; }

        /* Drawer CTA buttons */
        .drawer-cta {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          min-width: 0;
          padding: 0.95rem 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          font-weight: 600;
          letter-spacing: 0.03em;
          text-transform: none;
          border-radius: 2px;
          text-align: center;
          white-space: normal;
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary);
          transition: background-color 0.25s ease;
        }
        .drawer-cta:hover {
          background: color-mix(in srgb, var(--vibe-color-secondary), black 12%);
        }
      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'header.interactive_map_show_if_exists': {
      label: 'Interactive Map: Show If Exists',
      type: 'text',
      description:
        'Expose the Interactive Map page in the navigation if this token or field contains a value (e.g. custom_engrain_sightmap_url). Leave blank to hide.',
      defaultValue: '',
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_apply_now_url',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.address_link': {
      label: 'Address Link',
      fields: {
        address: {
          label: 'Address',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '123 Main Street, Atlanta, GA 30313',
        },
        url: {
          label: 'URL',
          type: 'url',
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
    'header.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
